import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  clamp,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * VIVANA — fallbacks for the popup tunables that live in CSS
 * (`.zoom-dialog--popup` in snippets/product-media-gallery-content.liquid).
 * They are only used if the custom property cannot be parsed as a px length.
 */
const POPUP_FALLBACK_SCALE = 2;
const POPUP_FALLBACK_GUTTER = 16;
const POPUP_FALLBACK_FRAME = 8;

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} [zoomedGallery] - The zoomed media list. It is the scrolling element inside the popup.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /**
   * VIVANA — the on-page media element the popup's size is derived from.
   * @type {HTMLElement | null}
   */
  #sourceMedia = null;

  /**
   * VIVANA — the element focus returns to when the popup closes.
   * @type {HTMLElement | null}
   */
  #triggerElement = null;

  /**
   * VIVANA — the media index the popup is currently sized for. Guards against re-sizing (and
   * therefore re-aligning) on every scroll event while the same image is on show.
   * @type {number | null}
   */
  #sizedIndex = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);

    // VIVANA — inside the popup the <ul> is the scroller rather than the <dialog>, so the
    // active-thumbnail / high-resolution sync has to listen there too. Both listeners are
    // kept: only one of the two elements actually scrolls at any given breakpoint.
    this.refs.zoomedGallery?.addEventListener('scroll', this.handleScroll);

    // The popup is sized from the on-page image, so it has to be re-measured when the
    // viewport (and therefore that image) changes size while the popup is open.
    window.addEventListener('resize', this.handleWindowResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.zoomedGallery?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleWindowResize);
  }

  /**
   * VIVANA — reads one of the popup's CSS tunables as a number.
   *
   * @param {string} property - The custom property name.
   * @param {number} fallback - Value to use when the property is missing or not a number.
   * @returns {number} The parsed value.
   */
  #readNumericProperty(property, fallback) {
    const value = Number.parseFloat(getComputedStyle(this.refs.dialog).getPropertyValue(property));

    return Number.isFinite(value) ? value : fallback;
  }

  /**
   * VIVANA — the image the popup is measured against: the one that was clicked, falling back to
   * whichever gallery image is currently visible (the source can be hidden, e.g. when the grid
   * presentation is showing and the slideshow is not).
   *
   * @returns {HTMLImageElement | null} The image, or null when nothing measurable is on screen.
   */
  #findSourceImage() {
    const gallery = this.closest('media-gallery');
    /** @type {(Element | null)[]} */
    const candidates = [
      this.#sourceMedia,
      ...(gallery?.querySelectorAll(
        'slideshow-slide:not([inert]) img.product-media__image, .media-gallery__grid img.product-media__image'
      ) ?? []),
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;

      const image =
        candidate instanceof HTMLImageElement ? candidate : candidate.querySelector('img.product-media__image');

      if (!(image instanceof HTMLImageElement)) continue;

      const rect = image.getBoundingClientRect();

      if (rect.width > 1 && rect.height > 1) return image;
    }

    return null;
  }

  /**
   * VIVANA — the aspect ratio of one of the zoomed media items, so the popup can be shaped by the
   * image it is actually showing. Falls back to the ratio Liquid put on the container
   * (`--media-preview-ratio`) while a lazy image has not decoded yet.
   *
   * @param {number} index - The media index.
   * @returns {number | null} The ratio, or null when it cannot be determined.
   */
  #mediaRatio(index) {
    const container = this.refs.media[index];

    if (!container) return null;

    const image = container.querySelector('img.product-media__image');

    if (image instanceof HTMLImageElement && image.naturalWidth && image.naturalHeight) {
      return image.naturalWidth / image.naturalHeight;
    }

    const declared = Number.parseFloat(container.style.getPropertyValue('--media-preview-ratio'));

    return Number.isFinite(declared) && declared > 0 ? declared : null;
  }

  /**
   * VIVANA — publishes the popup's box as CSS custom properties.
   *
   * Size comes from the on-page image (its rendered size multiplied by `--zoom-popup-scale`);
   * shape comes from the aspect ratio of the media the popup is *showing*. Two consequences,
   * both intentional: the gallery can be set to `media_fit: cover`, and using the real ratio
   * means the whole image is revealed with nothing cropped; and because the box follows the
   * shown media, switching images re-shapes the popup instead of letterboxing them inside the
   * first image's box. `Math.max` of the two scaled dimensions keeps the popup at least
   * `--zoom-popup-scale` times the on-page size on both axes. It is then scaled down as one to
   * fit the viewport minus the gutter, frame and thumbnail strip, so the aspect ratio survives
   * and small viewports simply get a smaller popup.
   *
   * When nothing can be measured the properties are left alone and the CSS fallbacks render.
   *
   * @param {number} [index] - The media index to size for. Defaults to the one already sized.
   */
  #updatePopupSize(index = this.#sizedIndex ?? 0) {
    const image = this.#findSourceImage();

    if (!image) return;

    const rect = image.getBoundingClientRect();
    const sourceRatio =
      image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : rect.width / rect.height;
    const ratio = this.#mediaRatio(index) ?? sourceRatio;
    const scale = this.#readNumericProperty('--zoom-popup-scale', POPUP_FALLBACK_SCALE);
    const inset =
      this.#readNumericProperty('--zoom-popup-gutter', POPUP_FALLBACK_GUTTER) +
      this.#readNumericProperty('--zoom-popup-frame', POPUP_FALLBACK_FRAME);

    // The thumbnail strip is a reserved row under the image, so its height is not available
    // to the image. The CSS zeroes --zoom-popup-strip when a product has no thumbnails.
    const strip = this.#readNumericProperty('--zoom-popup-strip', 0);

    let width = Math.max(rect.width, rect.height * ratio) * scale;
    let height = width / ratio;

    const factor = Math.min(
      1,
      Math.max(window.innerWidth - inset * 2, 1) / width,
      Math.max(window.innerHeight - inset * 2 - strip, 1) / height
    );

    width *= factor;
    height *= factor;

    this.style.setProperty('--zoom-popup-width', `${Math.round(width)}px`);
    this.style.setProperty('--zoom-popup-height', `${Math.round(height)}px`);
    this.#sizedIndex = index;
  }

  /**
   * VIVANA — keeps the popup proportional to the (now resized) on-page image.
   */
  handleWindowResize = debounce(() => {
    if (!this.refs.dialog.open) return;

    this.#updatePopupSize();
  }, 100);

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    // VIVANA — the popup is measured from the image that was clicked, and must be sized before
    // showModal() so it is correct on the first paint (and for the view transition below).
    // `activeElement` is remembered so focus can be returned to it on close.
    this.#sourceMedia = sourceImage;
    this.#triggerElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.#updatePopupSize(index);

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-current', `${i === activeIndex}`);
    });

    // VIVANA — re-shape the popup for the image that is now on show, then re-snap to it because
    // the item widths just changed. Guarded by #sizedIndex, so a scroll that stays on the same
    // image does no work and the instant re-align cannot feed itself.
    if (activeIndex !== this.#sizedIndex) {
      this.#updatePopupSize(activeIndex);
      mostVisibleElement.scrollIntoView({ behavior: 'instant' });
    }

    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());

    // VIVANA — return focus to whatever opened the popup. Browsers do this for `dialog.close()`
    // already, but it is explicit here because the popup can also be closed from a click on the
    // backdrop, where the trigger is no longer the last focused element.
    if (this.#triggerElement?.isConnected) this.#triggerElement.focus({ preventScroll: true });
    this.#triggerElement = null;
  }

  /**
   * VIVANA — closes the popup when the click lands outside the image.
   *
   * The declarative `on:click` binding only routes an event to this handler when the <dialog> is
   * the closest `[on:click]` ancestor of what was clicked — that is the ::backdrop (clicks on a
   * modal dialog's backdrop target the dialog itself) and the popup's own frame. Clicks on the
   * media and on the thumbnail buttons hit their own handlers instead.
   */
  handleDialogClick() {
    this.close();
  }

  /**
   * Closes the dialog when the user presses the escape key, or moves between media with the
   * left/right arrow keys.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    // VIVANA — the popup shows one image at a time, so the arrow keys navigate the set.
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    const { media } = this.refs;
    if (media.length < 2) return;

    const index = clamp(this.#activeIndex + (event.key === 'ArrowRight' ? 1 : -1), 0, media.length - 1);
    if (index === this.#activeIndex) return;

    event.preventDefault();
    this.selectThumbnail(index, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /**
   * VIVANA — the index of the media currently shown in the popup, read from the thumbnails so it
   * stays in step with `selectThumbnail` and `handleScroll`.
   *
   * @returns {number} The active index.
   */
  get #activeIndex() {
    const buttons = Array.from(this.refs.thumbnails.querySelectorAll('button'));
    const index = buttons.findIndex((button) => button.getAttribute('aria-current') === 'true');

    return index < 0 ? 0 : index;
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-current', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      // VIVANA — shape the popup for the image being selected before scrolling to it, so the
      // box and the scroll offset settle together instead of the image letterboxing inside
      // the previous one's box.
      this.#updatePopupSize(index);

      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
